import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { formatCurrency } from '../../utils/formatters';
import ErrorModal from './ErrorModal';
import { supabase } from '../../lib/supabase';
import { getCurrentArgentinaISOString } from '../../utils/dateUtils';

interface BalanceAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BalanceAdjustmentModal: React.FC<BalanceAdjustmentModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { setError: setGlobalError } = useAppStore();
  
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [date, setDate] = useState<string>(getCurrentArgentinaISOString());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setPaymentMethod('cash');
      setAmount('');
      setDescription('');
      setDate(getCurrentArgentinaISOString());
      setError(null);
    }
  }, [isOpen]);
  
  // Payment method options
  const paymentMethodOptions = [
    { value: 'cash', label: 'Efectivo' },
    { value: 'transfer', label: 'Transferencia' },
    { value: 'card', label: 'Tarjeta' },
    { value: 'check', label: 'Cheque' },
    { value: 'mercadopago', label: 'MercadoPago' },
  ];
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!paymentMethod) {
      setError('Debe seleccionar un medio de pago');
      setShowErrorModal(true);
      return;
    }
    
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) === 0) {
      setError('Debe ingresar un monto válido diferente de cero');
      setShowErrorModal(true);
      return;
    }
    
    if (!date) {
      setError('Debe seleccionar una fecha');
      setShowErrorModal(true);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // 1. Create activity log for the adjustment
      const { error: activityError } = await supabase
        .from('activity_logs')
        .insert([{
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action: `Ajuste de saldo de ${getPaymentMethodName(paymentMethod)}`,
          entity_type: 'payment_method',
          entity_id: null,
          details: `Ajuste de saldo: ${formatCurrency(parseFloat(amount))}${description ? ` - ${description}` : ''}`,
          action_type: 'update'
        }]);
      
      if (activityError) throw activityError;
      
      // 2. Create balance adjustment record
      const { error: adjustmentError } = await supabase
        .from('balance_adjustments')
        .insert([{
          payment_method: paymentMethod,
          amount: parseFloat(amount),
          description: description || null,
          date: date,
        }]);
      
      if (adjustmentError) {
        // If the balance_adjustments table doesn't exist yet, try to refresh the schema
        if (adjustmentError && adjustmentError.message && adjustmentError.message.includes('does not exist')) {
          // Create the table
          await supabase.rpc('force_schema_refresh');
          
          // Try again
          const { error: retryError } = await supabase
            .from('balance_adjustments')
            .insert([{
              payment_method: paymentMethod,
              amount: parseFloat(amount),
              description: description || null,
              date: date,
            }]);
          
          if (retryError) throw retryError;
        } else {
          throw adjustmentError;
        }
      }
      
      // Close modal
      onClose();
      
      // Refresh the page to show the new adjustment
      window.location.reload();
    } catch (error: any) {
      console.error('Error adjusting balance:', error);
      setError(error.message || 'Error al ajustar el saldo');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get payment method display name
  const getPaymentMethodName = (method: string): string => {
    const option = paymentMethodOptions.find(opt => opt.value === method);
    return option ? option.label : method;
  };
  
  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg">
            <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Ajuste de Saldo de Medio de Pago
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <Select
                  label="Medio de Pago"
                  value={paymentMethod}
                  onChange={setPaymentMethod}
                  options={paymentMethodOptions}
                  required
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Monto
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                      step="0.01"
                      className="w-full h-12 pl-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 dark:text-gray-400">
                      $
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Ingrese un valor positivo para aumentar el saldo o negativo para disminuirlo
                  </p>
                </div>
                
                <Input
                  type="date"
                  label="Fecha"
                  id="date"
                  name="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Descripción
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    rows={3}
                    placeholder="Motivo del ajuste (opcional)"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !paymentMethod || !amount || isNaN(parseFloat(amount)) || parseFloat(amount) === 0}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Procesando...
                    </>
                  ) : (
                    'Aplicar Ajuste'
                  )}
                </Button>
              </div>
            </form>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close"
                disabled={isLoading}
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />
    </>
  );
};

export default BalanceAdjustmentModal;